<template>
    <div class="project-page-container">
        <div class="project-hero">
            <img class="project-hero-img scanzy-hero-img" src="../assets/iphone-angle.png" alt="Scanzy">
            <h1 class="project-h1">Scanzy Barcode Scanning SDK</h1>
            <div class="project-hero-subtext">
                <p class="project-contribution">Brand Identity & Design System</p>
                <p class="project-contribution">Web Design & Development</p>
                <p class="project-contribution">Mobile App Design & Development</p>
            </div>
        </div>
        <div class="project-background light-green">
            <div class="project-background-inner-container">
                <h2 class="project-background-h2 project-description-h2">
                    At InventoryLab, I took Scanzy, an <span class="bold">AI-powered barcode scanning SDK</span>, from an internal tool with no public presence to a fully launched product with its own brand, marketing website, and mobile demo app.
                    <br><br>
                    I owned the entire user-facing experience: brand identity, information architecture, interaction design, and front-end development, growing the user base from <span class="bold">zero to nearly 16,000</span> in year one.
                </h2>
            </div>
        </div>

        <!-- Business Impact -->
        <div class="project-background">
            <div class="project-background-inner-container">
                <h2 class="project-background-h2">End Result & Business Impact</h2>
                <div class="business-impact">
                    <div class="business-impact-imgs">
                        <img class="scanzy-phone-mockup" src="../assets/scanzy-mobile-app-mockup.png" alt="Scanzy Mobile App">
                        <img class="scanzy-web-mockup" src="../assets/scanzy-web-mockup.png" alt="Scanzy Website">
                    </div>
                    <div class="business-impact-text">
                        <div>
                            <div>&bull;</div>
                            <div>Designed Scanzy's brand identity and visual design system, positioning it as a simple, low-cost alternative in a market dominated by complex enterprise SDKs.</div>
                        </div>
                        <div>
                            <div>&bull;</div>
                            <div>Designed and developed a Vue.js marketing and account-management website covering onboarding, billing, and integration guidance. Validated through developer user testing.</div>
                        </div>
                        <div>
                            <div>&bull;</div>
                            <div>Built an Ionic-based mobile demo app for iOS and Android showcasing scanning performance and customizable settings, giving developers a hands-on evaluation path.</div>
                        </div>
                        <div>
                            <div>&bull;</div>
                            <div>Grew user base from zero to nearly <span class="bold">16,000</span> within the first year of launch.</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Problem -->
        <div class="project-background-inner-container">
            <h2 class="project-background-h2">Problem</h2>
            <p class="project-background-p">
                Scanzy had a technically strong barcode scanning engine, but no way for developers to discover it, evaluate it, or integrate it. There was no brand, no public presence, no onboarding path, and no way to convert interest into adoption. The product existed only inside InventoryLab's internal stack.
            </p>
        </div>

        <!-- Goals & Deliverables -->
        <div class="project-background-inner-container not-first-cream-background">
            <h2 class="project-background-h2">Goals & Deliverables</h2>
            <p class="project-background-p">
                1. <span class="bold-span">Brand & Design System:</span> A cohesive visual identity and component library developers would trust. Accessible typography, a purposeful color palette, and UI patterns consistent across web and mobile.
                <br><br>
                2. <span class="bold-span">Marketing & Account Website:</span> A Vue.js site that clearly communicated Scanzy's value proposition, supported account creation, managed billing, and guided developers through SDK integration.
                <br><br>
                3. <span class="bold-span">Mobile Demo App:</span> An Ionic-based iOS and Android app letting developers test barcode scanning performance and configure settings in real time. This removed friction from the evaluation process.
            </p>
        </div>

        <!-- Design Process -->
        <div class="project-background-inner-container not-first-cream-background">
            <h2 class="project-background-h2">Design Process</h2>
            <div class="business-impact">
                <div class="business-impact-text">
                    <div>
                        <div>&bull;</div>
                        <div><span class="bold">Competitive analysis & user research:</span> Audited competing SDK products and interviewed developers to understand what builds trust in a new SDK, how they evaluate tools before committing, and where onboarding typically breaks down.</div>
                    </div>
                    <div>
                        <div>&bull;</div>
                        <div><span class="bold">Information architecture & user flows:</span> Mapped the developer journey from discovery → evaluation → integration → account management. Defined the IA for both the marketing site and the account portal before designing any screens.</div>
                    </div>
                    <div>
                        <div>&bull;</div>
                        <div><span class="bold">Wireframes → Prototypes → Testing:</span> Iterated through wireframes and interactive prototypes for the website and mobile app. Tested navigation, onboarding clarity, and demo app settings with developers. Refined based on direct feedback.</div>
                    </div>
                    <div>
                        <div>&bull;</div>
                        <div><span class="bold">Brand identity & design system:</span> Developed Scanzy's visual identity (logo, color palette, typography, and button/link patterns) as a reusable system rather than one-off assets, ensuring consistency as the product grew.</div>
                    </div>
                    <div>
                        <div>&bull;</div>
                        <div><span class="bold">Accessibility standards:</span> Applied WCAG contrast and typography guidelines throughout the design system, ensuring the site and app were usable across devices and lighting conditions (especially important for a barcode scanning demo context).</div>
                    </div>
                    <div>
                        <div>&bull;</div>
                        <div><span class="bold">Design to development:</span> Built the final web and mobile experiences myself in Vue.js and Ionic, ensuring the implemented product matched the validated designs without translation loss.</div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Branding & Style -->
        <div class="project-background-inner-container not-first-cream-background">
            <h2 class="project-background-h2">Branding & Design System</h2>
            <div class="project-logo-container">
                <img class="project-img scanzy within-project-scanzy-logo" src="../assets/scanzy.png" alt="Scanzy Logo">
            </div>
            <div class="scanzy-colors">
                <img class="scanzy-color" src="../assets/color-scanzy-orange.png" alt="Burnt orange">
                <img class="scanzy-color" src="../assets/color-scanzy-green.png" alt="Blue green">
                <img class="scanzy-color blue-green-light" src="../assets/color-scanzy-green-light.png" alt="Blue green light">
                <img class="scanzy-color" src="../assets/color-scanzy-cream.png" alt="Cream">
            </div>
            <div class="scanzy-colors mobile-colors">
                <div>
                    <img class="scanzy-color" src="../assets/color-scanzy-orange.png" alt="Burnt orange">
                    <img class="scanzy-color" src="../assets/color-scanzy-green.png" alt="Blue green">
                </div>
                <div>
                    <img class="scanzy-color blue-green-light" src="../assets/color-scanzy-green-light.png" alt="Blue green light">
                    <img class="scanzy-color" src="../assets/color-scanzy-cream.png" alt="Cream">
                </div>
            </div>
            <div class="project-img-container">
                <img class="scanzy-official" src="../assets/official.png" alt="Official typeface specimen">
                <img class="scanzy-official poppins" src="../assets/poppins.png" alt="Poppins typeface specimen">
            </div>
            <div class="scanzy-button-link">
                <img class="scanzy-button" src="../assets/scanzy-button.png" alt="Scanzy button component">
                <img class="scanzy-link" src="../assets/scanzy-link.png" alt="Scanzy link component">
            </div>
        </div>

        <!-- Mobile App -->
        <div class="project-background-inner-container not-first-cream-background">
            <h2 class="project-background-h2">Mobile Demo App</h2>
            <p class="project-background-p">
                The demo app needed to do one thing well: let developers feel how fast and accurate the scanner was, and understand how to configure it for their own use case. Screens were kept deliberately minimal.  The technology is the feature.
            </p>
            <div class="scanzy-mobile-mockups-container">
                <img class="scanzy-app-mockup settings" src="../assets/phone-dark.png" alt="Scanzy Demo App scanning view">
                <img class="scanzy-app-mockup settings" src="../assets/scanzy-settings.png" alt="Scanzy Demo App settings screen">
            </div>
        </div>

        <div class="project-background-inner-container not-first-cream-background">
            <h2 class="project-background-h2">Thanks for reading!</h2>
        </div>
        <div class="project-background-inner-container not-first-cream-background back-to-top-container">
            <a @click="scrollToTop">Back to top</a>
            <router-link to="/projects">More projects</router-link>
        </div>
    </div>
</template>

<script>
export default {
    name: 'ScanzyPage',
    data: function() {
        return {};
    },
    methods: {
        scrollToTop() {
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        }
    }
}
</script>